import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import SpeechInput from './SpeechInput';

/**
 * Reusable AI Conversation Component
 *
 * Props:
 * - title: string - Title shown at top of conversation
 * - subtitle: string - Optional subtitle/description
 * - initialMessage: string - First message from AI to start conversation
 * - onSendMessage: async (message, history) => response - Function to send message and get AI response
 * - onComplete: (history) => void - Called when conversation ends (user clicks Done or AI ends it)
 * - onCancel: () => void - Called when user cancels/goes back
 * - maxExchanges: number - Maximum number of user messages before suggesting to wrap up (default 10)
 * - doneButtonText: string - Text for the done button (default "I'm Done - Generate Summary")
 * - placeholder: string - Placeholder text for input field
 */
function AIConversation({
  title = "AI Conversation",
  subtitle = "",
  initialMessage = "",
  onSendMessage,
  onComplete,
  onCancel,
  maxExchanges = 10,
  doneButtonText = "I'm Done - Generate Summary",
  placeholder = "Type your response..."
}) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const inputBeforeSpeechRef = useRef(''); // Track text before speech (ref avoids closure issues)
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [aiSignaledComplete, setAiSignaledComplete] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize with first AI message
  useEffect(() => {
    if (initialMessage && messages.length === 0) {
      setMessages([{ role: 'assistant', content: initialMessage }]);
    }
  }, [initialMessage, messages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input after AI responds
  useEffect(() => {
    if (!isLoading && !isComplete && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, isComplete]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading || isComplete) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setError('');

    // Add user message to history
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setExchangeCount(prev => prev + 1);
    setIsLoading(true);

    try {
      // Call the provided onSendMessage function
      const response = await onSendMessage(userMessage, newMessages);

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      // Add AI response
      const updatedMessages = [...newMessages, { role: 'assistant', content: response.message }];
      setMessages(updatedMessages);

      // Check if AI signaled conversation is complete
      // Don't auto-advance - let user read the final message and click Continue
      if (response.isComplete) {
        setAiSignaledComplete(true);
      }
    } catch (err) {
      setError('Failed to get response. Please try again.');
      console.error('Conversation error:', err);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDone = () => {
    setIsComplete(true);
    onComplete(messages);
  };

  const handleSaveConversation = () => {
    // Format conversation as readable text
    const timestamp = new Date().toLocaleString();
    let text = `${title}\n`;
    text += `${'='.repeat(title.length)}\n`;
    text += `Saved: ${timestamp}\n\n`;

    messages.forEach((msg) => {
      const speaker = msg.role === 'assistant' ? 'AI Advisor' : 'You';
      text += `--- ${speaker} ---\n`;
      text += `${msg.content}\n\n`;
    });

    text += `\n--- End of Conversation ---\n`;

    // Create and download file
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product-strategy-conversation-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatMessage = (content) => {
    // Simple formatting: convert **text** to bold, preserve line breaks
    return content.split('\n').map((line, i) => {
      // Handle bold text
      const parts = line.split(/(\*\*.*?\*\*)/g);
      const formatted = parts.map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      return (
        <span key={i}>
          {formatted}
          {i < content.split('\n').length - 1 && <br />}
        </span>
      );
    });
  };

  return (
    <Card className="shadow">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h4 className="mb-1">{title}</h4>
            {subtitle && <p className="text-muted small mb-0">{subtitle}</p>}
          </div>
          {exchangeCount > 0 && (
            <span className="badge bg-secondary">
              {exchangeCount} / {maxExchanges} exchanges
            </span>
          )}
        </div>

        {/* Messages Container */}
        <div
          className="border rounded p-3 mb-3"
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            backgroundColor: '#f8f9fa'
          }}
        >
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`mb-3 ${msg.role === 'user' ? 'text-end' : ''}`}
            >
              <div
                className={`d-inline-block p-3 rounded ${
                  msg.role === 'user'
                    ? 'bg-primary text-white'
                    : 'bg-white border'
                }`}
                style={{
                  maxWidth: '85%',
                  textAlign: 'left'
                }}
              >
                {msg.role === 'assistant' && (
                  <small className="text-muted d-block mb-1">
                    <strong>🤖 AI Advisor</strong>
                  </small>
                )}
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {formatMessage(msg.content)}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="mb-3">
              <div className="d-inline-block p-3 rounded bg-white border">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted">Thinking...</span>
              </div>
            </div>
          )}

          {isComplete && (
            <div className="text-center">
              <Alert variant="success" className="d-inline-block">
                <Spinner animation="border" size="sm" className="me-2" />
                Generating your strategy summary...
              </Alert>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        {/* Near max exchanges warning */}
        {exchangeCount >= maxExchanges - 2 && exchangeCount < maxExchanges && !isComplete && (
          <Alert variant="warning" className="mb-3 py-2 small">
            <strong>Heads up:</strong> You're approaching the end of the conversation.
            Click "{doneButtonText}" when you're ready to generate your summary.
          </Alert>
        )}

        {/* Max exchanges reached */}
        {exchangeCount >= maxExchanges && !isComplete && !aiSignaledComplete && (
          <Alert variant="info" className="mb-3 py-2">
            <strong>Time to wrap up!</strong> You've had a thorough discussion.
            Click the button below to generate your strategy summary.
          </Alert>
        )}

        {/* AI signaled conversation is complete - show continue prompt */}
        {aiSignaledComplete && !isComplete && (
          <Alert variant="success" className="mb-3">
            <strong>Great conversation!</strong> The AI has gathered enough insights.
            Take a moment to read the final message above, then click Continue when ready.
          </Alert>
        )}

        {/* Input Area - normal mode */}
        {!isComplete && !aiSignaledComplete && (
          <>
            <Form.Group className="mb-3">
              <div className="position-relative">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={placeholder}
                  disabled={isLoading || exchangeCount >= maxExchanges}
                  ref={inputRef}
                  style={{ paddingRight: '50px' }}
                />
                <SpeechInput
                  onStart={() => {
                    // Save current text before speech starts
                    inputBeforeSpeechRef.current = inputValue;
                  }}
                  onTranscript={(text, isFinal) => {
                    // Show text in real-time (use ref to get current value)
                    const prefix = inputBeforeSpeechRef.current ? inputBeforeSpeechRef.current + ' ' : '';
                    setInputValue(prefix + text);
                  }}
                  onError={(msg) => setError(msg)}
                  disabled={isLoading || exchangeCount >= maxExchanges}
                  className="position-absolute"
                  style={{ right: '8px', bottom: '8px' }}
                  size="sm"
                />
              </div>
              <Form.Text className="text-muted">
                Press Enter to send, Shift+Enter for new line. Click the mic to speak.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={onCancel}
                disabled={isLoading}
              >
                ← Back to Products
              </Button>

              <div>
                {/* Save conversation button */}
                {messages.length >= 2 && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleSaveConversation}
                    className="me-2"
                    title="Download conversation as text file"
                    size="sm"
                  >
                    💾 Save
                  </Button>
                )}

                {/* Skip/End Early button - less prominent */}
                <Button
                  variant="outline-primary"
                  onClick={handleDone}
                  disabled={isLoading || messages.length < 2}
                  className="me-2"
                  size="sm"
                >
                  Skip → Generate Now
                </Button>

                {/* Send button - primary action, at the end */}
                {exchangeCount < maxExchanges && (
                  <Button
                    variant="success"
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      'Send →'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* AI signaled complete - show action buttons */}
        {aiSignaledComplete && !isComplete && (
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={onCancel}
            >
              ← Back to Products
            </Button>

            <div>
              <Button
                variant="outline-info"
                onClick={handleSaveConversation}
                className="me-2"
                title="Download conversation as text file"
              >
                Save Conversation
              </Button>

              <Button
                variant="success"
                onClick={handleDone}
                size="lg"
              >
                Continue to Strategy Summary →
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default AIConversation;
